/**
 * Network interceptor for development testing.
 * Adds artificial latency to async operations when VITE_SYNC_LATENCY_MS is set.
 */

class NetworkInterceptor {
  private readonly latencyMs: number;
  private readonly enabled: boolean;

  constructor() {
    const envLatency = import.meta.env.VITE_SYNC_LATENCY_MS;
    this.latencyMs = envLatency ? parseInt(envLatency, 10) : 0;
    this.enabled = this.latencyMs > 0 && import.meta.env.DEV;
  }

  /**
   * Intercept an async function and add artificial latency.
   * Only active in development when VITE_SYNC_LATENCY_MS > 0.
   */
  wrap<T, Args extends unknown[]>(
    fn: (...args: Args) => Promise<T>,
  ): (...args: Args) => Promise<T> {
    if (!this.enabled) {
      return fn;
    }

    return async (...args: Args): Promise<T> => {
      await new Promise((resolve) => setTimeout(resolve, this.latencyMs));
      return fn(...args);
    };
  }
}

// Singleton instance
let instance: NetworkInterceptor | null = null;

export function getNetworkInterceptor(): NetworkInterceptor {
  if (!instance) {
    instance = new NetworkInterceptor();
  }
  return instance;
}
